/**
 * Hand-match one of TODAY's bank credits to the existing loan being settled (Dean, 30 Oct 2026 —
 * Scotluso paid quoting an old ref and the Contract Wizard's settle figure missed it).
 * The match is the same record the Daily Collections Report uses, so both screens agree.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Link2, Unlink, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const gbp = (n) => `£${(parseFloat(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TodaysPaymentPicker({ open, onClose, loan, onMatched }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [meta, setMeta] = useState({ date: "", source: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/loandisk/todays-feed-payments", { params: { loan_id: loan?.loanId, sf_number: loan?.sfNumber }, timeout: 60000 });
      setRows(data?.transactions || []);
      setMeta({ date: data?.date, source: data?.source });
    } catch (err) {
      toast.error("Could not load today's payments: " + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setQ(""); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^£/, "");
    if (!s) return rows;
    return rows.filter((r) => (r.description || "").toLowerCase().includes(s) || String(r.amount).includes(s)
      || (r.sf_refs || []).some((x) => x.toLowerCase().includes(s)) || (r.bank_account || "").toLowerCase().includes(s));
  }, [rows, q]);

  const match = async (r) => {
    setBusy(r.id);
    try {
      await api.post("/collections/manual-match", {
        loan_id: String(loan.loanId), transaction_id: r.id, date: meta.date || undefined,
        loan_sf: loan.sfNumber, borrower_name: loan.borrowerName, transaction_description: r.description,
      });
      toast.success(`${gbp(r.amount)} matched to ${loan.sfNumber}`);
      await load();
      onMatched?.();
    } catch (err) {
      toast.error("Match failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(null);
    }
  };

  const unmatch = async (r) => {
    setBusy(r.id);
    try {
      await api.delete(`/collections/manual-match/${encodeURIComponent(String(loan.loanId))}/${encodeURIComponent(r.id)}`);
      toast.success("Match removed");
      await load();
      onMatched?.();
    } catch (err) {
      toast.error("Unmatch failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="todays-payment-picker">
        <DialogHeader>
          <DialogTitle className="text-base">Match a payment received today → {loan?.sfNumber}</DialogTitle>
          <DialogDescription className="text-xs">
            Every credit in today's bank feed ({meta.date || "today"}). Pick the one {loan?.borrowerName || "this customer"} sent — it is deducted from the balance to settle and the Daily Collections Report shows it as matched too.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description, SF#, amount or bank…" className="pl-8 h-9" data-testid="payment-picker-search" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded border divide-y" data-testid="payment-picker-list">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading today's payments…</div>
          ) : visible.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="payment-picker-empty">{rows.length ? "No payment matches that search." : "No credits in today's feed yet."}</p>
          ) : visible.map((r) => (
            <div key={r.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${r.manual_match === "this_loan" ? "bg-emerald-50" : ""}`} data-testid={`payment-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{r.description || "—"}</p>
                <p className="text-[11px] text-muted-foreground">
                  {r.bank_account || "Bank"} · {r.time_added || ""}{r.status ? ` · ${r.status}` : ""}
                  {r.sf_refs?.length ? <span className="ml-1 font-mono">· {r.sf_refs.join(", ")}</span> : null}
                  {r.manual_match === "other_loan" && <span className="ml-1 text-amber-700">· already matched to loan {r.manual_loan_id}</span>}
                  {r.manual_match === "this_loan" && <span className="ml-1 text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> matched to this loan</span>}
                </p>
              </div>
              <span className="font-mono tabular-nums shrink-0">{gbp(r.amount)}</span>
              {r.manual_match === "this_loan" ? (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={busy === r.id} onClick={() => unmatch(r)} data-testid={`payment-unmatch-${r.id}`}>
                  {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />} Unmatch
                </Button>
              ) : r.manual_match === "other_loan" ? (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled title={`Already hand-matched to loan ${r.manual_loan_id} on the Daily Collections Report — unmatch it there first`} data-testid={`payment-taken-${r.id}`}>
                  Taken
                </Button>
              ) : (
                <Button type="button" size="sm" className="h-7 text-xs" disabled={busy === r.id} onClick={() => match(r)} data-testid={`payment-match-${r.id}`}>
                  {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />} Match
                </Button>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">{rows.length} credit{rows.length === 1 ? "" : "s"} today — the same list the Daily Collections Report works from{meta.source === "portal" ? "" : " (via Airtable)"}.</p>
      </DialogContent>
    </Dialog>
  );
}
